import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSmoothScrollToItemOptions<T> {
  items: T[];
  selectedId: string | undefined;
  itemHeight: number;
  getItemId: (item: T) => string;
  delay?: number;
}

export const useSmoothScrollToItem = <T>({
  items,
  selectedId,
  itemHeight,
  getItemId,
  delay = 100,
}: UseSmoothScrollToItemOptions<T>) => {
  const outerReference = useRef<HTMLDivElement>(null);
  const previousSelectedId = useRef<string | undefined>(undefined);
  const getItemIdReference = useRef(getItemId);
  const [isReferenceReady, setIsReferenceReady] = useState(false);

  const outerReferenceCallback = useCallback((node: HTMLDivElement | null) => {
    outerReference.current = node;
    setIsReferenceReady(!!node);
  }, []);

  useEffect(() => {
    getItemIdReference.current = getItemId;
  }, [getItemId]);

  useEffect(() => {
    if (!selectedId || !isReferenceReady || selectedId === previousSelectedId.current || items.length === 0) {
      return;
    }

    const index = items.findIndex(item => getItemIdReference.current(item) === selectedId);
    if (index === -1 || !outerReference.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!outerReference.current) return;

      const scrollTop = index * itemHeight;
      const containerHeight = outerReference.current.clientHeight;
      const centeredScrollTop = scrollTop - containerHeight / 2 + itemHeight / 2;

      outerReference.current.scrollTo({
        top: Math.max(0, centeredScrollTop),
        behavior: 'smooth',
      });

      previousSelectedId.current = selectedId;
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [selectedId, items, itemHeight, delay, isReferenceReady]);

  return { outerRef: outerReferenceCallback };
};
